import React, { useEffect, useLayoutEffect, useState } from 'react';

import { useDecidePlaceholderType } from '@/utilities/helpers/clientPlaceholder';
import { GameClientPlaceholder } from '@/game-client/anexes/GameClientPlaceholder';

import useClientWrapperEffects from '@/game-client/hooks/use-client-wrapper-effects';
import useGlsInitializeUrlDelivery from '@/game-client/hooks/use-gls-initialize-url-delivery';
import useJackpotModeGlsReconcile from '@/game-client/hooks/use-jackpot-mode-gls-reconcile';
import { useAppOrientation } from '@/lostHooks/orientation/useOrientation';
import { useStore } from '@/store/useStore';
import { areUserSessionTokensReadyForGame } from '@/utilities/helpers/areUserSessionTokensReadyForGame';
import { useRemoveInitialLoader } from '@/lostHooks/use-remove-initial-loader/useRemoveInitialLoader';

const GameClientLoadingWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const userSessionContext = useStore((s) => s.userSessionContext);
  const { isAndroid, isXsell } = useStore((s) => s.runtime?.wrapperType) ?? {
    isAndroid: false,
    isXsell: false,
  };
  const isSessionReadyForGame = areUserSessionTokensReadyForGame(
    userSessionContext,
    isAndroid || isXsell,
  );
  const gameLoadStarted = useStore((s) => s.gameStatus.gameLoadStarted);
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const themeData = useStore((s) => s.commonUIContext.themeData);

  const orientation = useAppOrientation();
  useRemoveInitialLoader();
  const [loadingAnimationFinished, setLoadingAnimationFinished] =
    useState(false);

  const prevGameLoadStartedRef = React.useRef(gameLoadStarted);
  const { isStuckedLoading } = useClientWrapperEffects();
  useGlsInitializeUrlDelivery();
  useJackpotModeGlsReconcile();

  const prevSessionReadyRef = React.useRef(isSessionReadyForGame);
  const prevGameResetRevisionRef = React.useRef(gameResetRevision);

  // Soft reload (try again / recently played): remount loading UI before paint.
  useLayoutEffect(() => {
    if (prevGameResetRevisionRef.current === gameResetRevision) {
      return;
    }
    prevGameResetRevisionRef.current = gameResetRevision;
    queueMicrotask(() => setLoadingAnimationFinished(false));
  }, [gameResetRevision]);

  // Reset loading animation when session becomes not ready
  // Note: setState in effect is intentional here for transition detection (prev → current)
  useEffect(() => {
    const sessionBecameNotReady =
      prevSessionReadyRef.current && !isSessionReadyForGame;

    if (sessionBecameNotReady) {
      queueMicrotask(() => setLoadingAnimationFinished(false));
    }

    prevSessionReadyRef.current = isSessionReadyForGame;
  }, [isSessionReadyForGame]);

  // Reset loading animation when game load restarts
  // Note: setState in effect is intentional here for transition detection (prev → current)
  useEffect(() => {
    if (prevGameLoadStartedRef.current && !gameLoadStarted) {
      queueMicrotask(() => setLoadingAnimationFinished(false));
    }
    prevGameLoadStartedRef.current = gameLoadStarted;
  }, [gameLoadStarted]);

  return (
    <>
      <GameClientPlaceholder
        setLoadingAnimationFinished={setLoadingAnimationFinished}
        loadingAnimationFinished={loadingAnimationFinished}
        isStuckedLoading={isStuckedLoading}
        type={useDecidePlaceholderType(
          isStuckedLoading,
          loadingAnimationFinished,
          isSessionReadyForGame,
        )}
        themeData={themeData}
        orientation={orientation}
      />
      {children}
    </>
  );
};

export default GameClientLoadingWrapper;
